import { type JSX, useMemo, useState } from "react";

import { translate } from "app/filters/i18nFilter";
import type { IDropDownItem } from "app/pages/app-intelligence/app-analysis/types";
import { CrmCell } from "app/pages/sales-intelligence/common-components/export-table/cells/CrmCell";
import { DeleteCell } from "app/pages/sales-intelligence/common-components/export-table/cells/DeleteCell/DeleteCell";
import { ExampleCell } from "app/pages/sales-intelligence/common-components/export-table/cells/ExampleCell";
import { OverwriteCell } from "app/pages/sales-intelligence/common-components/export-table/cells/OverwriteCell";
import { SimilarwebCell } from "app/pages/sales-intelligence/common-components/export-table/cells/SimilarwebCell";
import { ADD_FIELD } from "app/pages/sales-intelligence/common-components/export-table/const";
import { INTERNAL_ADD_ITEM_TEXT } from "app/pages/sales-intelligence/common-components/export-table/constants";
import { getIntegrationNameFromUrl } from "app/pages/sales-intelligence/common-components/export-table/getIntegrationNameFromUrl";
import {
    StyledEmptyStateWrapper,
    StyledTable,
} from "app/pages/sales-intelligence/common-components/export-table/styles";
import {
    createPlacement,
    getSwFieldColumnName,
    isPreDefinedField,
} from "app/pages/sales-intelligence/common-components/export-table/utils";
import TechnographicsCell from "app/pages/sales-intelligence/modules/technology/components/TechnographicsCell";
import usePrefetchTechnologiesDescription from "app/pages/sales-intelligence/modules/technology/hooks/usePrefetchTechnologiesDescription";
import integrationTrackingService from "app/pages/sales-intelligence/services/tracking/integrationTrackingService";
import type {
    ExportItem,
    ExportName,
    SalesforcePreDefinedType,
} from "app/pages/sales-intelligence/sub-modules/management/store/type";
import type { Integration } from "app/pages/sales-intelligence/sub-modules/management/types";
import { convertIntegrationTypeForApi } from "app/pages/sales-intelligence/sub-modules/management/utils/utils";
import Table from "app/pages/workspace/sales/components/custom-table/Table/Table";

import { useExportTableService } from "../../hooks/useExportTableService";
import { useManageExportTable } from "../../hooks/useManageExportTable";
import { DeleteFieldModal } from "../DeleteFieldModal/DeleteFieldModal";

export const ExportTable = ({ exportName }: { exportName: ExportName }) => {
    const { integrationName } = getIntegrationNameFromUrl() as { integrationName: Integration };

    const [manuallyFocusedIdx, setManuallyFocusedIdx] = useState<number | null>(null);

    usePrefetchTechnologiesDescription();

    const {
        handleDeleteClick,
        onCrmCellClick,
        onToggleClick,
        onSimilarwebCellClick,
        onExampleChange,
        getCrmFields,
        tableData,
        isCrmFieldsLoading,
        similarwebFields,
        crmOptions,
        columns,
        isColumnsLoading,
        selectedCrmFields,
        focusIdx,
    } = useManageExportTable(integrationName, exportName);
    const dataLength = tableData.length;

    const exportTableService = useExportTableService(exportName);

    const searchFilter = (term: string, item: JSX.Element) => {
        integrationTrackingService.trackSearch(term, exportName, integrationName);
        return (item.props as IDropDownItem).text.toLowerCase().includes(term.toLowerCase());
    };

    const renderSimilarwebField = (setting: ExportItem, index: number) => {
        const { title } = setting;
        const isRequired = exportTableService.isPrimaryRequiredFieldInstance(setting, tableData);

        const { placement } = createPlacement(dataLength, index);

        return (
            <SimilarwebCell
                index={index}
                exportName={exportName}
                setting={setting}
                similarwebFields={similarwebFields}
                onClick={onSimilarwebCellClick}
                title={title}
                searchFilter={searchFilter}
                isRequired={isRequired}
                placement={placement}
            />
        );
    };

    const renderExample = (setting: ExportItem, index: number) => {
        const { example, metadata, externalField } = setting;

        const isVisible = externalField !== INTERNAL_ADD_ITEM_TEXT;
        const isDisabled = !externalField;

        const onChange = (value: string) => onExampleChange(setting, value);

        const onClick = () => setManuallyFocusedIdx(index);

        const onBlur = () => setManuallyFocusedIdx(null);

        return isVisible ? (
            <ExampleCell
                key={index}
                onClick={onClick}
                onBlur={onBlur}
                focusIdx={manuallyFocusedIdx || focusIdx}
                index={index}
                tableLength={dataLength}
                exportName={exportName}
                externalField={externalField}
                setting={setting}
                example={example}
                value={metadata}
                onChange={onChange}
                isDisabled={isDisabled}
                columns={columns}
                TechnographicsCellComponent={TechnographicsCell}
            />
        ) : null;
    };

    const renderCrm = (setting: ExportItem, index: number) => {
        const { externalField, sourceType } = setting;

        const isPreDefined = isPreDefinedField(setting);
        const swColumnName = getSwFieldColumnName(setting);

        const handleToggle = (isOpen: boolean) => {
            if (!isOpen) {
                setManuallyFocusedIdx(null);
                return;
            }
            index !== focusIdx && setManuallyFocusedIdx(index);

            getCrmFields(
                swColumnName,
                sourceType,
                isPreDefined,
                convertIntegrationTypeForApi(exportName) as SalesforcePreDefinedType,
            );
        };

        const onClick = (newSetting: { id: string; text: string }) => {
            integrationTrackingService.trackMapField(integrationName, exportName, newSetting.text);
            onCrmCellClick(setting, newSetting);
        };

        return (
            <CrmCell
                onClick={onClick}
                handleToggle={handleToggle}
                index={index}
                focusIdx={manuallyFocusedIdx || focusIdx}
                exportName={exportName}
                tableLength={dataLength}
                isPreDefined={isPreDefined}
                isLoading={isCrmFieldsLoading}
                isColumnsLoading={isColumnsLoading}
                field={setting.field}
                selectedCrmField={externalField}
                options={crmOptions}
                columns={columns}
                selectedCrmFields={selectedCrmFields}
            />
        );
    };

    const renderOverwrite = (setting: ExportItem) => {
        const { field, overwrite } = setting;
        const isRequired = exportTableService.isPrimaryRequiredFieldInstance(setting, tableData) || field === ADD_FIELD;

        const handleOnOverwriteClick = (setting: ExportItem, value: boolean) => {
            integrationTrackingService.trackOverwriteSWField(integrationName, exportName, value, setting.title);
            onToggleClick({ setting, key: "overwrite", value });
        };

        const tooltipContent = exportTableService.getOverwriteCellTooltipContent(overwrite);

        return (
            <OverwriteCell
                setting={setting}
                isRequired={isRequired}
                onClick={handleOnOverwriteClick}
                overwrite={overwrite}
                tooltipContent={tooltipContent}
            />
        );
    };

    const renderDelete = (setting: ExportItem) => {
        const { field } = setting;
        const isVisible = !exportTableService.isPrimaryRequiredFieldInstance(setting, tableData) && field !== ADD_FIELD;
        if (!isVisible) {
            return null;
        }

        return (
            <DeleteCell
                setting={setting}
                handleDelete={handleDeleteClick}
            />
        );
    };

    const tableColumns = useMemo(
        () =>
            exportName === "companyAsLeads"
                ? exportTableService.getColumns({ ignoredColumns: ["forExport", "forEnrichment", "overwrite"] })
                : exportTableService.getColumns({ ignoredColumns: ["forExport", "forEnrichment"] }),
        [exportName, exportTableService],
    );

    return (
        <>
            <StyledTable>
                <Table
                    hasStickyHeader
                    emptyState={
                        <StyledEmptyStateWrapper>
                            {translate("si.management.integration.export.table.empty.text")}
                        </StyledEmptyStateWrapper>
                    }
                    data={tableData}
                    columns={tableColumns}
                    customRenderers={{
                        title: renderSimilarwebField,
                        example: renderExample,
                        externalField: renderCrm,
                        overwrite: renderOverwrite,
                        delete: renderDelete,
                    }}
                    customHeaderRenderers={{
                        title: exportTableService.renderCustomHeaderCell("title", dataLength),
                        externalField: exportTableService.renderCustomHeaderCell("externalField"),
                        overwrite: exportTableService.renderCustomHeaderCell("overwrite"),
                        example: exportTableService.renderCustomHeaderCell("example"),
                    }}
                />
            </StyledTable>

            <DeleteFieldModal exportName={exportName} />
        </>
    );
};
